import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Button, Switch, CircularProgress, Divider, IconButton, Chip, Collapse, Alert, Card, CardContent, CardHeader, Tooltip } from '@mui/material';
import { ArrowBack, ArrowForward, Save, ExpandMore, ExpandLess, Security, ToggleOn, ToggleOff } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import axiosInstance from '../../services/axiosInstance';

interface Permission {
  permissionId: number;
  permissionCode: string;
  permissionName: string;
  moduleName: string;
  description?: string;
}

interface Role {
  roleId: number;
  roleName: string;
  roleNameAr?: string;
  description?: string;
}

interface GroupedPermissions {
  [module: string]: Permission[];
}

const translations = {
  en: {
    title: 'Manage Role Permissions',
    role: 'Role',
    loading: 'Loading...',
    save: 'Save Changes',
    back: 'Back to Roles',
    selectAll: 'Select All',
    deselectAll: 'Deselect All',
    permissions: 'Permissions',
    granted: 'Granted',
    denied: 'Denied',
    saveSuccess: 'Permissions saved successfully',
    saveError: 'Failed to save permissions',
    loadError: 'Failed to load data',
    noPermissions: 'No permissions available',
    module: 'Module',
    expandAll: 'Expand All',
    collapseAll: 'Collapse All',
    enableAll: 'Enable All',
    disableAll: 'Disable All',
    permissionsCount: 'permissions',
    enabled: 'Enabled',
    disabled: 'Disabled',
  },
  ar: {
    title: 'إدارة صلاحيات الدور',
    role: 'الدور',
    loading: 'جاري التحميل...',
    save: 'حفظ التغييرات',
    back: 'العودة للأدوار',
    selectAll: 'تفعيل الكل',
    deselectAll: 'إلغاء الكل',
    permissions: 'الصلاحيات',
    granted: 'ممنوحة',
    denied: 'محرومة',
    saveSuccess: 'تم حفظ الصلاحيات بنجاح',
    saveError: 'فشل في حفظ الصلاحيات',
    loadError: 'فشل في تحميل البيانات',
    noPermissions: 'لا توجد صلاحيات متاحة',
    module: 'الموديول',
    expandAll: 'توسيع الكل',
    collapseAll: 'طي الكل',
    enableAll: 'تفعيل الكل',
    disableAll: 'إلغاء الكل',
    permissionsCount: 'صلاحية',
    enabled: 'مفعّل',
    disabled: 'معطّل',
  }
};

const RolePermissionsPage: React.FC = () => {
  const { roleId } = useParams<{ roleId: string }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { mode } = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';
  const isDark = mode === 'dark';

  const [role, setRole] = useState<Role | null>(null);
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [grantedPermissionIds, setGrantedPermissionIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadData();
  }, [roleId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load role details
      const roleRes = await axiosInstance.get(`/Roles/${roleId}`);
      setRole(roleRes.data.data || roleRes.data);

      // Load all permissions
      const permRes = await axiosInstance.get('/Permissions');
      const perms = permRes.data.data || permRes.data || [];
      setAllPermissions(perms);

      // Load role's current permissions
      const rolePermsRes = await axiosInstance.get(`/Roles/${roleId}/permissions`);
      const rolePerms = rolePermsRes.data.data || rolePermsRes.data || [];
      const grantedIds = new Set<number>(rolePerms.map((p: Permission) => p.permissionId));
      setGrantedPermissionIds(grantedIds);

      // Expand all modules by default
      const modules = new Set<string>(perms.map((p: Permission) => p.moduleName));
      setExpandedModules(modules);

    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error(t.loadError);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePermission = (permissionId: number) => {
    setGrantedPermissionIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(permissionId)) {
        newSet.delete(permissionId);
      } else {
        newSet.add(permissionId);
      }
      return newSet;
    });
  };

  const handleToggleModule = (moduleName: string, permissions: Permission[]) => {
    const modulePermIds = permissions.map(p => p.permissionId);
    const allGranted = modulePermIds.every(id => grantedPermissionIds.has(id));
    
    setGrantedPermissionIds(prev => {
      const newSet = new Set(prev);
      if (allGranted) {
        modulePermIds.forEach(id => newSet.delete(id));
      } else {
        modulePermIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    setGrantedPermissionIds(new Set(allPermissions.map(p => p.permissionId)));
  };

  const handleDeselectAll = () => {
    setGrantedPermissionIds(new Set());
  };

  const handleExpandAll = () => {
    setExpandedModules(new Set(allPermissions.map(p => p.moduleName)));
  };

  const handleCollapseAll = () => {
    setExpandedModules(new Set());
  };

  const handleToggleExpand = (moduleName: string) => {
    setExpandedModules(prev => {
      const newSet = new Set(prev);
      if (newSet.has(moduleName)) {
        newSet.delete(moduleName);
      } else {
        newSet.add(moduleName);
      }
      return newSet;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axiosInstance.put(`/Roles/${roleId}/permissions`, Array.from(grantedPermissionIds));
      toast.success(t.saveSuccess);
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error(t.saveError);
    } finally {
      setSaving(false);
    }
  };

  // Group permissions by module
  const groupedPermissions: GroupedPermissions = allPermissions.reduce((acc, perm) => {
    const module = perm.moduleName || 'Other';
    if (!acc[module]) acc[module] = [];
    acc[module].push(perm);
    return acc;
  }, {} as GroupedPermissions);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{t.loading}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3, 
          background: isDark 
            ? 'linear-gradient(135deg, #1e2a4a 0%, #2d3a5c 50%, #3d4a6c 100%)' 
            : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
          color: 'white', 
          borderRadius: 3,
          boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(79,172,254,0.3)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate('/settings/roles')} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
              {isRTL ? <ArrowForward /> : <ArrowBack />}
            </IconButton>
            <Box sx={{ 
              width: 56, 
              height: 56, 
              borderRadius: 2, 
              bgcolor: 'rgba(255,255,255,0.15)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <Security sx={{ fontSize: 32 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight="bold">{t.title}</Typography>
              {role && (
                <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                  {t.role}: <strong>{isRTL && role.roleNameAr ? role.roleNameAr : role.roleName}</strong>
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              bgcolor: 'rgba(255,255,255,0.15)', 
              borderRadius: 2, 
              px: 2, 
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Typography variant="h4" fontWeight="bold">{grantedPermissionIds.size}</Typography>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', lineHeight: 1 }}>/ {allPermissions.length}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>{t.permissions}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Action Buttons */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, 
          mb: 3, 
          bgcolor: isDark ? 'rgba(30,42,74,0.5)' : 'rgba(255,255,255,0.8)', 
          borderRadius: 2,
          border: `1px solid ${isDark ? 'rgba(79,172,254,0.15)' : 'rgba(0,0,0,0.08)'}`,
          backdropFilter: 'blur(10px)',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleSelectAll} 
              startIcon={<ToggleOn />}
              sx={{ 
                borderColor: '#22c55e', 
                color: '#22c55e',
                '&:hover': { borderColor: '#16a34a', bgcolor: 'rgba(34,197,94,0.1)' }
              }}
            >
              {t.selectAll}
            </Button>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleDeselectAll} 
              startIcon={<ToggleOff />}
              sx={{ 
                borderColor: '#ef4444', 
                color: '#ef4444',
                '&:hover': { borderColor: '#dc2626', bgcolor: 'rgba(239,68,68,0.1)' }
              }}
            >
              {t.deselectAll}
            </Button>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleExpandAll} 
              startIcon={<ExpandMore />}
              sx={{ 
                borderColor: isDark ? '#4facfe' : '#0284c7', 
                color: isDark ? '#4facfe' : '#0284c7',
              }}
            >
              {t.expandAll}
            </Button>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleCollapseAll} 
              startIcon={<ExpandLess />}
              sx={{ 
                borderColor: isDark ? '#4facfe' : '#0284c7', 
                color: isDark ? '#4facfe' : '#0284c7',
              }}
            >
              {t.collapseAll}
            </Button>
          </Box>
          <Button 
            variant="contained" 
            onClick={handleSave} 
            disabled={saving} 
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
            sx={{ 
              background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
              px: 4,
              py: 1,
              fontWeight: 600,
              boxShadow: '0 4px 15px rgba(34,197,94,0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                boxShadow: '0 6px 20px rgba(34,197,94,0.4)',
              }
            }}
          >
            {t.save}
          </Button>
        </Box>
      </Paper>

      {/* Permissions List */}
      {Object.keys(groupedPermissions).length === 0 ? (
        <Alert severity="info">{t.noPermissions}</Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {Object.entries(groupedPermissions).map(([moduleName, permissions]) => {
            const modulePermIds = permissions.map(p => p.permissionId);
            const grantedCount = modulePermIds.filter(id => grantedPermissionIds.has(id)).length;
            const allGranted = grantedCount === permissions.length;
            const someGranted = grantedCount > 0 && !allGranted;
            const isExpanded = expandedModules.has(moduleName);

            return (
              <Card 
                key={moduleName}
                elevation={0}
                sx={{ 
                  borderRadius: 3,
                  border: `1px solid ${isDark ? 'rgba(79,172,254,0.2)' : 'rgba(0,0,0,0.08)'}`,
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: isDark ? '0 8px 32px rgba(79,172,254,0.15)' : '0 8px 32px rgba(0,0,0,0.08)',
                    borderColor: isDark ? 'rgba(79,172,254,0.4)' : 'rgba(79,172,254,0.3)',
                  }
                }}
              >
                  {/* Module Header */}
                  <CardHeader
                    sx={{ 
                      background: isDark 
                        ? 'linear-gradient(135deg, rgba(79,172,254,0.15) 0%, rgba(0,242,254,0.08) 100%)'
                        : 'linear-gradient(135deg, rgba(79,172,254,0.1) 0%, rgba(0,242,254,0.05) 100%)',
                      cursor: 'pointer',
                      py: 1.5,
                      '&:hover': { 
                        background: isDark 
                          ? 'linear-gradient(135deg, rgba(79,172,254,0.2) 0%, rgba(0,242,254,0.12) 100%)'
                          : 'linear-gradient(135deg, rgba(79,172,254,0.15) 0%, rgba(0,242,254,0.08) 100%)',
                      }
                    }}
                    onClick={() => handleToggleExpand(moduleName)}
                    title={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ color: isDark ? '#4facfe' : '#0284c7' }}>
                          {moduleName}
                        </Typography>
                        <Chip 
                          size="small" 
                          label={`${grantedCount}/${permissions.length}`}
                          sx={{
                            fontWeight: 600,
                            bgcolor: allGranted 
                              ? 'rgba(34,197,94,0.2)' 
                              : someGranted 
                                ? 'rgba(251,191,36,0.2)' 
                                : 'rgba(148,163,184,0.2)',
                            color: allGranted 
                              ? '#22c55e' 
                              : someGranted 
                                ? '#f59e0b' 
                                : isDark ? '#94a3b8' : '#64748b',
                          }}
                        />
                      </Box>
                    }
                    action={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Master Switch for Module */}
                        <Tooltip title={allGranted ? t.disableAll : t.enableAll}>
                          <Box 
                            onClick={(e) => { e.stopPropagation(); handleToggleModule(moduleName, permissions); }}
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              bgcolor: allGranted 
                                ? 'rgba(34,197,94,0.15)' 
                                : someGranted 
                                  ? 'rgba(251,191,36,0.15)'
                                  : 'rgba(148,163,184,0.1)',
                              borderRadius: 2,
                              px: 1.5,
                              py: 0.5,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                bgcolor: allGranted 
                                  ? 'rgba(34,197,94,0.25)' 
                                  : 'rgba(79,172,254,0.2)',
                              }
                            }}
                          >
                            <Switch
                              checked={allGranted}
                              size="small"
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#22c55e',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: '#22c55e',
                                },
                              }}
                            />
                            <Typography variant="caption" fontWeight={600} sx={{ ml: 0.5, color: isDark ? '#94a3b8' : '#64748b' }}>
                              {allGranted ? t.enabled : t.disabled}
                            </Typography>
                          </Box>
                        </Tooltip>
                        <IconButton size="small" sx={{ color: isDark ? '#4facfe' : '#0284c7' }}>
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Box>
                    }
                  />

                  {/* Permissions Grid */}
                  <Collapse in={isExpanded}>
                    <CardContent sx={{ pt: 2, pb: 3 }}>
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: { 
                          xs: '1fr', 
                          sm: 'repeat(2, 1fr)', 
                          md: 'repeat(3, 1fr)', 
                          lg: 'repeat(4, 1fr)' 
                        }, 
                        gap: 2 
                      }}>
                        {permissions.map(permission => {
                          const isGranted = grantedPermissionIds.has(permission.permissionId);
                          return (
                            <Box
                              key={permission.permissionId}
                              onClick={() => handleTogglePermission(permission.permissionId)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1.5,
                                borderRadius: 2,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                border: `1px solid ${isGranted 
                                  ? 'rgba(34,197,94,0.3)' 
                                  : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                                bgcolor: isGranted 
                                  ? isDark ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.05)'
                                  : 'transparent',
                                '&:hover': {
                                  bgcolor: isGranted 
                                    ? isDark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.1)'
                                    : isDark ? 'rgba(79,172,254,0.1)' : 'rgba(79,172,254,0.05)',
                                  borderColor: isGranted 
                                    ? 'rgba(34,197,94,0.5)' 
                                    : isDark ? 'rgba(79,172,254,0.3)' : 'rgba(79,172,254,0.2)',
                                  transform: 'translateY(-1px)',
                                }
                              }}
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography 
                                  variant="body2" 
                                  fontWeight={isGranted ? 600 : 400}
                                  sx={{ 
                                    color: isGranted 
                                      ? isDark ? '#4ade80' : '#16a34a'
                                      : isDark ? '#e2e8f0' : '#334155',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {permission.permissionName}
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: isDark ? '#64748b' : '#94a3b8',
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {permission.permissionCode}
                                </Typography>
                              </Box>
                              <Switch
                                checked={isGranted}
                                size="small"
                                sx={{
                                  ml: 1,
                                  '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: '#22c55e',
                                  },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: '#22c55e',
                                  },
                                }}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    </CardContent>
                  </Collapse>
                </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default RolePermissionsPage;